import React, { useState } from 'react';
import Button from 'src/components/Button';
import CenterModal from 'src/components/CenterModal';
import Input from 'src/components/Input';
import ModalHeader from 'src/components/ModalHeader';
import { MfaChallenge } from 'src/api/mfa';
import { useSendEmailVerificationChallenge, useVerifyEmail } from 'src/queries/useMfa';
import { parseError } from 'src/utils';
import styles from './styles.module.scss';

interface MfaEmailVerificationLinkProps {
    destination?: string | null;
    className?: string;
}

function MfaEmailVerificationLink({ destination, className }: MfaEmailVerificationLinkProps) {
    const sendEmailVerification = useSendEmailVerificationChallenge();
    const verifyEmail = useVerifyEmail();
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
    const [emailChallenge, setEmailChallenge] = useState<MfaChallenge | null>(null);
    const [emailCode, setEmailCode] = useState('');
    const [emailError, setEmailError] = useState('');

    async function handleStartEmailVerification() {
        try {
            setEmailError('');
            setEmailCode('');
            const challenge = await sendEmailVerification.mutateAsync();
            setEmailChallenge(challenge);
            setIsVerifyingEmail(true);
        } catch (e) {
            setEmailError(parseError(e));
        }
    }

    async function handleVerifyEmail() {
        if (!emailChallenge) {
            return;
        }

        try {
            setEmailError('');
            await verifyEmail.mutateAsync({ challengeId: emailChallenge.challengeId, code: emailCode.trim() });
            setIsVerifyingEmail(false);
            setEmailChallenge(null);
            setEmailCode('');
        } catch (e) {
            setEmailError(parseError(e));
        }
    }

    return (
        <>
            <button
                className={`${styles.verifyLink} ${className || ''}`.trim()}
                disabled={sendEmailVerification.isLoading}
                onClick={handleStartEmailVerification}
                type="button"
            >
                {sendEmailVerification.isLoading ? 'Sending...' : 'Verify email address'}
            </button>
            {emailError && !isVerifyingEmail ? <div className={styles.error}>{emailError}</div> : null}
            {isVerifyingEmail ? (
                <CenterModal
                    isOpen={isVerifyingEmail}
                    onClose={() => setIsVerifyingEmail(false)}
                    cardStyle={styles.modalCard}
                >
                    <ModalHeader title="Verify account email" onClose={() => setIsVerifyingEmail(false)} />
                    <div className={styles.modalBody}>
                        <div className={styles.description}>
                            Enter the verification code sent to {emailChallenge?.destination || destination}.
                        </div>
                        <Input
                            label="Verification code"
                            value={emailCode}
                            onChangeText={setEmailCode}
                            autoFocus={true}
                            maxLength={12}
                        />
                        {emailError ? <div className={styles.error}>{emailError}</div> : null}
                        <div className={styles.modalActions}>
                            <Button type="transparent" text="Cancel" onClick={() => setIsVerifyingEmail(false)} />
                            <Button
                                type="primary"
                                text="Verify"
                                onClick={handleVerifyEmail}
                                disabled={!emailCode.trim()}
                                isLoading={verifyEmail.isLoading}
                            />
                        </div>
                    </div>
                </CenterModal>
            ) : null}
        </>
    );
}

export default MfaEmailVerificationLink;
